import React from "react";
import Navbar from "../Hero/Navbar/Navbar";
import Footer from "../Footer/Footer";
import { Shield, Lock, Eye, Database, Server, MessageCircle, AlertTriangle, HelpCircle } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <>
    <Navbar/>
    <div className="min-h-screen bg-gray-50 text-gray-800 mt-16">
      <header className="bg-indigo-600 text-white py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
          <p className="mt-2 text-indigo-100">Understanding how we protect your data at QuizAI</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <section className="mb-8">
          <div className="flex items-center mb-4">
            <Shield className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-2xl font-semibold">Our Commitment to Privacy</h2>
          </div>
          <p className="mb-4">
            At QuizAI, we take your privacy seriously. This policy describes what personal information we collect, how we use it, and your rights regarding your data.
          </p>
          <p>
            Last updated: April 16, 2025
          </p>
        </section>

        <section className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <Database className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-xl font-semibold">Information We Collect</h2>
          </div>
          <div className="ml-8">
            <h3 className="font-semibold mb-2">Personal Information</h3>
            <ul className="list-disc ml-6 mb-4">
              <li>Account information (name, email, username)</li>
              <li>Profile information you choose to provide</li>
              <li>Authentication data</li>
            </ul>

            <h3 className="font-semibold mb-2">Quiz Activity</h3>
            <ul className="list-disc ml-6 mb-4">
              <li>Quiz performance and scores</li>
              <li>Topics and categories you engage with</li>
              <li>AI-generated quiz responses</li>
            </ul>

            <h3 className="font-semibold mb-2">Usage Data</h3>
            <ul className="list-disc ml-6">
              <li>Device information and IP address</li>
              <li>Log data and time spent on platform</li>
              <li>Feature usage patterns</li>
            </ul>
          </div>
        </section>

        <section className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <Server className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-xl font-semibold">How We Use Your Information</h2>
          </div>
          <ul className="ml-8 space-y-2">
            <li><span className="font-medium">To Provide Our Service:</span> Creating personalized quiz experiences and AI-generated content</li>
            <li><span className="font-medium">To Improve Our Platform:</span> Analyzing usage patterns to enhance features</li>
            <li><span className="font-medium">To Personalize Content:</span> Tailoring quiz topics and difficulty based on your preferences</li>
            <li><span className="font-medium">To Train Our AI Models:</span> Improving quiz generation and answer evaluation capabilities</li>
            <li><span className="font-medium">To Communicate:</span> Sending updates about new features and services</li>
          </ul>
        </section>

        <section className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <Lock className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-xl font-semibold">Data Security</h2>
          </div>
          <p className="mb-4">
            We implement industry-standard security measures to protect your personal information:
          </p>
          <ul className="ml-8 list-disc">
            <li>Encryption of sensitive data in transit and at rest</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and authentication procedures</li>
            <li>Secure data storage with regular backups</li>
          </ul>
        </section>

        <section className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <Eye className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-xl font-semibold">AI Processing & Data Usage</h2>
          </div>
          <p className="mb-4">
            Our quiz application utilizes artificial intelligence to generate questions, evaluate answers, and provide personalized learning experiences. Here's how we handle data in relation to our AI systems:
          </p>
          <ul className="ml-8 space-y-2">
            <li><span className="font-medium">Quiz Content Generation:</span> Our AI creates quiz questions based on selected topics and difficulty levels</li>
            <li><span className="font-medium">Performance Analysis:</span> We process your answers to provide feedback and track progress</li>
            <li><span className="font-medium">Model Training:</span> Anonymized quiz interactions may be used to improve our AI models</li>
            <li><span className="font-medium">Data Retention:</span> AI-generated content and interactions are stored securely</li>
          </ul>
        </section>

        <section className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <MessageCircle className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-xl font-semibold">Third-Party Sharing</h2>
          </div>
          <p className="mb-4">
            We may share information with the following types of third parties:
          </p>
          <ul className="ml-8 space-y-2">
            <li><span className="font-medium">Service Providers:</span> Cloud hosting, analytics, and payment processors</li>
            <li><span className="font-medium">AI Technology Partners:</span> For enhancing quiz generation capabilities</li>
            <li><span className="font-medium">Educational Partners:</span> When you opt into specific educational programs or features</li>
          </ul>
          <p className="mt-4 text-sm text-gray-600">
            We never sell your personal information to advertisers or data brokers.
          </p>
        </section>

        <section className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <HelpCircle className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-xl font-semibold">Your Privacy Rights</h2>
          </div>
          <p className="mb-4">
            Depending on your location, you may have the right to:
          </p>
          <ul className="ml-8 list-disc">
            <li>Access or request a copy of your personal information</li>
            <li>Correct inaccurate information</li>
            <li>Delete your data (subject to certain exceptions)</li>
            <li>Object to certain processing activities</li>
            <li>Export your data in a portable format</li>
            <li>Opt out of AI processing of your quiz responses</li>
          </ul>
        </section>

        <section className="mb-8 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center mb-4">
            <AlertTriangle className="mr-2 text-indigo-600" size={24} />
            <h2 className="text-xl font-semibold">Changes to This Policy</h2>
          </div>
          <p>
            We may update this privacy policy to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes through the application or via email.
          </p>
        </section>

        <section className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4">Contact Us</h2>
          <p className="mb-4">
            If you have questions or concerns about this privacy policy or our data practices, please contact us at:
          </p>
          <div className="bg-gray-50 p-4 rounded">
            <p>info@quzy.in</p>
            <p>7B, 2nd Floor Street 2</p>
            <p>East Guru Angad Nagar, Delhi-110092</p>
            <p> Delhi-110092</p>
          </div>
        </section>
      </main>

      <footer className="bg-gray-100 py-6 mt-8 border-t border-gray-200">
        <div className="container mx-auto px-4 text-center text-sm text-gray-600">
          <p>© 2025 QuizAI App. All rights reserved.</p>
        </div>
      </footer>
    </div>
    <Footer/>

    </>
  );
}
